/**
 * Sports-themed brand backdrop: a football pitch in gentle perspective, lit by a
 * stadium floodlight and faded into the dark for depth. Shared by the landing
 * page, the workspace-gate splash and the auth page.
 *
 * Sleekness comes from *lighting*, not extra geometry:
 *   - a soft blurred glow pass under the crisp lines (floodlights on chalk),
 *   - a vertical depth mask so the field recedes toward the horizon + edges,
 *   - a refined floodlight glow and a gentle vignette to focus the center.
 *
 * Pitch lines use `currentColor` (bound to `text-foreground`) so they stay
 * visible in both light and dark themes. Render inside a `relative` container —
 * this fills it as an absolute layer.
 */

// The pitch geometry, authored once and drawn twice (glow + crisp) via <use>.
const PITCH_LINES = (
  <g fill="none" stroke="currentColor" strokeLinejoin="round" strokeLinecap="round">
    {/* outline, halfway line, center circle */}
    <polygon points="20,238 360,238 285,76 95,76" />
    <line x1="61" y1="150" x2="319" y2="150" />
    <ellipse cx="190" cy="150" rx="36" ry="14" />
    {/* near goal: penalty box + goal box */}
    <polygon points="105,238 275,238 266,200 114,200" />
    <polygon points="145,238 235,238 233,221 147,221" />
    {/* far goal: penalty box + goal box */}
    <polygon points="143,76 238,76 244,106 136,106" />
    <polygon points="165,76 215,76 217,89 163,89" />
  </g>
)

// Fade the field in from the horizon and out at the bottom edge → depth.
const DEPTH_MASK =
  'linear-gradient(to bottom, transparent 2%, black 36%, black 84%, transparent 100%)'

export function PitchBackdrop() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      {/* Stadium floodlight — a soft cool-blue glow spilling from above. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 55% 50% at 50% -12%, rgba(24, 69, 200, 0.3), transparent 70%)',
        }}
      />

      {/* The pitch: glow pass beneath crisp pass, both faded by the depth mask. */}
      <div
        className="absolute inset-0"
        style={{ WebkitMaskImage: DEPTH_MASK, maskImage: DEPTH_MASK }}
      >
        <svg
          className="absolute inset-0 h-full w-full text-foreground"
          viewBox="0 0 380 240"
          preserveAspectRatio="xMidYMax slice"
        >
          <defs>
            {/* id used by the two <use> passes below */}
            <g id="pitch-lines">{PITCH_LINES}</g>
          </defs>
          {/* Soft glow pass disabled — it added visual busy-ness; kept
              (commented) for revival rather than deleted.
          <use
            href="#pitch-lines"
            strokeWidth={3.5}
            opacity={0.09}
            style={{ filter: 'blur(2.5px)' }}
          />
          */}
          {/* Crisp lines only — kept faint so the field reads as calm atmosphere. */}
          <use href="#pitch-lines" strokeWidth={1.25} opacity={0.18} />
        </svg>
      </div>

      {/* Gentle vignette — darkens the corners to focus the center and deepen
          the dark. Kept subtle so it reads on the light theme too. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 95% 85% at 50% 42%, transparent 62%, rgba(0, 0, 0, 0.22) 100%)',
        }}
      />
    </div>
  )
}
